<template>
  <div class="alice-det">
    <!-- ============== Sub-page header ============== -->
    <header class="alice-det__head">
      <div class="alice-det__head-main">
        <div class="alice-det__crumb">
          <span>Project</span>
          <Icon name="chevron-right" :size="12" />
          <span>Details</span>
        </div>
        <h1 class="alice-det__title">Details</h1>
        <p class="alice-det__subtitle">
          Onderzoekssetup, deelnemers, vraagstelling en context. Alle velden
          worden opgeslagen wanneer je op “Save changes” klikt.
        </p>
      </div>

      <!-- "Import setup PDF" temporarily hidden — script methods
           (triggerSetupUpload / onFileChange / uploadSetup) are kept for
           when this is re-enabled.
      <div class="alice-det__head-actions">
        <button
          class="alice-det__head-btn"
          :disabled="!project || importing"
          @click="triggerSetupUpload"
        >
          <Icon name="plus" :size="16" />
          <span>{{ importing ? "Importing…" : "Import setup PDF" }}</span>
        </button>
        <input
          ref="uploadRef"
          type="file"
          accept="application/pdf"
          style="display: none"
          @change="onFileChange"
          @click="resetUploadValue"
        />
      </div>
      -->
    </header>

    <div v-if="!project" class="alice-det__empty">
      <p>Loading project…</p>
    </div>

    <div v-else class="alice-det__body">
      <!-- ============== Identity ============== -->
      <section class="alice-det__section">
        <header class="alice-det__section-head">
          <span class="alice-det__section-mark" />
          <h2 class="alice-det__section-title">Identity</h2>
          <p class="alice-det__section-sub">
            Naam van de klant en titels die in het rapport verschijnen.
          </p>
        </header>
        <div class="alice-det__grid alice-det__grid--two">
          <label class="alice-det__field alice-det__field--span">
            <span class="alice-det__label">Project name</span>
            <input
              v-model="project.title"
              class="alice-det__input"
              type="text"
              placeholder="e.g. Mobile onboarding study Q2"
            />
          </label>
          <div class="alice-det__field">
            <span class="alice-det__label">Client</span>
            <div class="alice-det__client-row">
              <select
                :value="project.client_id || ''"
                class="alice-det__input alice-det__client-select"
                @change="onClientSelect($event.target.value)"
              >
                <option value="">— No client —</option>
                <option v-for="c in clients" :key="c.uid" :value="c.uid">
                  {{ c.name }}
                </option>
              </select>
              <v-menu
                v-model="clientMenu"
                :close-on-content-click="false"
                location="bottom end"
              >
                <template #activator="{ props }">
                  <button
                    type="button"
                    class="alice-det__add-client"
                    title="Create a new client"
                    v-bind="props"
                  >
                    <Icon name="plus" :size="14" />
                    <span>New</span>
                  </button>
                </template>
                <div class="alice-det__client-pop">
                  <div class="alice-det__client-pop-label">New client</div>
                  <input
                    v-model="newClientName"
                    class="alice-det__input"
                    type="text"
                    placeholder="Client name"
                    @keyup.enter="createAndSelectClient"
                  />
                  <button
                    type="button"
                    class="alice-det__client-pop-btn"
                    :disabled="!newClientName.trim()"
                    @click="createAndSelectClient"
                  >
                    Add client
                  </button>
                </div>
              </v-menu>
            </div>
            <span v-if="unlinkedName" class="alice-det__hint">
              Imported as “{{ unlinkedName }}” — not linked yet.
              <button
                type="button"
                class="alice-det__link-create"
                @click="createFromImported"
              >
                Create “{{ unlinkedName }}”
              </button>
            </span>
          </div>
          <!-- The Valsplat job number: saved on its own (setProjectJobNumber),
               which also links the job's Drive folder. -->
          <div class="alice-det__field">
            <span class="alice-det__label">Jobnummer</span>
            <form class="alice-det__job-row" @submit.prevent="saveJobNumber">
              <JobNumberPicker
                v-model="jobDraft"
                :client-id="project.client_id || null"
                :client-name="project.client_name || null"
                :disabled="jobSaving"
                placeholder="Bijv. D2303 (optioneel)"
                look="details"
                class="alice-det__job-picker"
                @pick="jobPick = $event"
              />
              <button
                type="submit"
                class="alice-det__add-client alice-det__job-save"
                :disabled="!jobDirty || Boolean(jobError) || jobSaving"
                title="Slaat het jobnummer direct op en koppelt de Drive-map van de job"
              >
                {{ jobSaving ? "Opslaan…" : "Opslaan" }}
              </button>
            </form>
            <span
              v-if="jobMessage"
              class="alice-det__hint alice-det__job-msg"
              :class="{ 'alice-det__job-msg--error': jobFailed }"
              >{{ jobMessage }}</span
            >
            <span v-else-if="jobDirty && !jobError" class="alice-det__hint">
              {{ jobPickLabel || "Nog niet opgeslagen." }}
            </span>
            <span v-else-if="savedRefLabel" class="alice-det__hint">
              {{ savedRefLabel }}
            </span>
            <span v-else-if="!project.project_code" class="alice-det__hint">
              Optioneel: het jobnummer uit Productive. Wordt direct opgeslagen
              en koppelt de Drive-map van de job.
            </span>
          </div>
          <label class="alice-det__field alice-det__field--span">
            <span class="alice-det__label">Report title</span>
            <input
              v-model="project.report_title"
              class="alice-det__input"
              type="text"
              placeholder="Klantonderzoek …"
            />
          </label>
          <label class="alice-det__field alice-det__field--span">
            <span class="alice-det__label">Report subtitle</span>
            <input
              v-model="project.report_subtitle"
              class="alice-det__input"
              type="text"
              placeholder="Optional"
            />
          </label>
        </div>
      </section>

      <!-- ============== Research setup ============== -->
      <section class="alice-det__section">
        <header class="alice-det__section-head">
          <span
            class="alice-det__section-mark"
            style="background: var(--vp-turquoise-100)"
          />
          <h2 class="alice-det__section-title">Research setup</h2>
          <p class="alice-det__section-sub">
            Methode, apparaat en deelnemerinformatie.
          </p>
        </header>

        <div class="alice-det__grid alice-det__grid--two">
          <label class="alice-det__field">
            <span class="alice-det__label">Type</span>
            <span class="alice-det__select-wrap">
              <select v-model="project.type" class="alice-det__select">
                <option value="">Select…</option>
                <option value="UX Test">UX Test</option>
                <option value="Survey">Survey</option>
                <option value="Interview">Interview</option>
                <option value="Diary study">Diary study</option>
              </select>
              <Icon name="chevron-down" :size="14" />
            </span>
          </label>
          <label class="alice-det__field">
            <span class="alice-det__label">Device</span>
            <span class="alice-det__select-wrap">
              <select v-model="project.device" class="alice-det__select">
                <option value="">Select…</option>
                <option value="Desktop">Desktop</option>
                <option value="Mobile">Mobile</option>
                <option value="Tablet">Tablet</option>
              </select>
              <Icon name="chevron-down" :size="14" />
            </span>
          </label>
          <label class="alice-det__field alice-det__field--span">
            <span class="alice-det__label">Method description</span>
            <input
              v-model="project.method_description"
              class="alice-det__input"
              type="text"
              placeholder="Korte methodebeschrijving"
            />
          </label>
          <label class="alice-det__field">
            <span class="alice-det__label">N participants</span>
            <input
              v-model.number="project.n"
              class="alice-det__input alice-det__input--mono"
              type="number"
              min="0"
              placeholder="0"
            />
          </label>
          <label class="alice-det__field">
            <span class="alice-det__label">Participants description</span>
            <input
              v-model="project.participants_description"
              class="alice-det__input"
              type="text"
              placeholder="Doelgroep, recruitment…"
            />
          </label>
          <label class="alice-det__field">
            <span class="alice-det__label">Test material</span>
            <input
              v-model="project.test_material"
              class="alice-det__input"
              type="text"
              placeholder="Wat is er getest?"
            />
          </label>
          <label class="alice-det__field">
            <span class="alice-det__label">Context</span>
            <input
              v-model="project.context"
              class="alice-det__input"
              type="text"
              placeholder="Bredere achtergrond"
            />
          </label>
        </div>
      </section>

      <!-- ============== Research questions ============== -->
      <section class="alice-det__section">
        <header class="alice-det__section-head">
          <span
            class="alice-det__section-mark"
            style="background: var(--vp-geel-100)"
          />
          <h2 class="alice-det__section-title">Research questions</h2>
          <p class="alice-det__section-sub">
            De hoofdvraag plus eventuele sub-vragen.
          </p>
        </header>

        <label class="alice-det__field">
          <span class="alice-det__label">Main research question</span>
          <textarea
            v-model="project.main_research_question"
            class="alice-det__textarea"
            rows="2"
            placeholder="Wat probeer je te achterhalen?"
          />
        </label>

        <div class="alice-det__field">
          <div class="alice-det__sub-head">
            <span class="alice-det__label">Sub research questions</span>
            <span class="alice-det__count-pill">{{ subQuestions.length }}</span>
          </div>
          <div v-for="(q, i) in subQuestions" :key="i" class="alice-det__row">
            <span class="alice-det__row-num">{{
              String(i + 1).padStart(2, "0")
            }}</span>
            <textarea
              v-model="q.text"
              class="alice-det__textarea alice-det__textarea--mini"
              rows="2"
              placeholder="Sub-vraag…"
            />
            <button
              class="alice-det__icon-btn alice-det__icon-btn--danger alice-det__row-del"
              title="Remove sub question"
              type="button"
              @click="removeSub(i)"
            >
              <Icon name="trash" :size="14" />
            </button>
          </div>
          <button class="alice-det__add-btn" type="button" @click="addSub">
            <Icon name="plus" :size="14" />
            <span>Add sub research question</span>
          </button>
        </div>
      </section>

      <!-- ============== Chat context ============== -->
      <section class="alice-det__section">
        <header class="alice-det__section-head">
          <span
            class="alice-det__section-mark"
            style="background: var(--vp-blauw-120)"
          />
          <h2 class="alice-det__section-title">Extra context for ChatGPT</h2>
          <p class="alice-det__section-sub">
            Achtergrond die aan ieder chatgesprek wordt toegevoegd.
          </p>
        </header>
        <label class="alice-det__field">
          <span class="alice-det__label">Context for this study</span>
          <textarea
            v-model="project.chat_context"
            class="alice-det__textarea"
            rows="5"
            placeholder="Bedrijfsachtergrond, jargon, doelen…"
          />
        </label>
      </section>

      <!-- ============== Researchers ============== -->
      <section class="alice-det__section">
        <header class="alice-det__section-head">
          <span
            class="alice-det__section-mark"
            style="background: var(--vp-rood-100)"
          />
          <h2 class="alice-det__section-title">Researchers</h2>
          <p class="alice-det__section-sub">
            Wie aan dit project werkt — verschijnt op het rapport.
          </p>
        </header>
        <div class="alice-det__researchers">
          <div
            v-for="(r, i) in researchers"
            :key="r.uid || r.email || i"
            class="alice-det__researcher"
          >
            <Avatar
              :name="r.name"
              :photo="r.photo || r.image"
              :color="researcherColor(r)"
              :size="36"
            />
            <div class="alice-det__researcher-text">
              <div class="alice-det__researcher-name">{{ r.name }}</div>
              <div v-if="r.email" class="alice-det__researcher-email">
                {{ r.email }}
              </div>
            </div>
            <button
              class="alice-det__icon-btn alice-det__icon-btn--danger"
              title="Remove researcher"
              type="button"
              @click="removeResearcher(i)"
            >
              <Icon name="trash" :size="14" />
            </button>
          </div>

          <v-menu v-if="availableUsers.length" :close-on-content-click="true">
            <template #activator="{ props: menuProps }">
              <button
                v-bind="menuProps"
                class="alice-det__add-btn alice-det__add-btn--solid"
              >
                <Icon name="plus" :size="14" />
                <span>Add researcher</span>
                <Icon name="chevron-down" :size="12" />
              </button>
            </template>
            <v-list class="alice-menu-list">
              <v-list-item
                v-for="(u, ui) in availableUsers"
                :key="u.uid || u.email || ui"
                @click="addResearcher(u)"
              >
                <v-list-item-title>{{ u.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="u.email">{{
                  u.email
                }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-menu>

          <p
            v-if="!researchers.length && !availableUsers.length"
            class="alice-det__hint"
          >
            No team members available yet.
          </p>
        </div>
      </section>

      <!-- ============== Guest access ============== -->
      <GuestAccessSection
        :project-id="project.uid"
        :project-title="project.title || ''"
      />

      <!-- ============== Next steps ============== -->
      <section class="alice-det__section">
        <header class="alice-det__section-head">
          <span
            class="alice-det__section-mark"
            style="background: var(--vp-paars-100)"
          />
          <h2 class="alice-det__section-title">Next steps</h2>
          <p class="alice-det__section-sub">
            Acties voor na de oplevering. Laat Alice voorstellen genereren op
            basis van je insights.
          </p>
          <button
            class="alice-det__head-btn alice-det__head-btn--small"
            type="button"
            @click="generateNextSteps"
          >
            <Icon name="star" :size="14" />
            <span>Generate</span>
          </button>
        </header>

        <div v-for="(s, i) in nextSteps" :key="i" class="alice-det__row">
          <span class="alice-det__row-num">{{
            String(i + 1).padStart(2, "0")
          }}</span>
          <textarea
            v-model="s.text"
            class="alice-det__textarea alice-det__textarea--mini"
            rows="2"
            placeholder="Volgende stap…"
          />
          <button
            class="alice-det__icon-btn alice-det__icon-btn--danger alice-det__row-del"
            title="Remove step"
            type="button"
            @click="removeNextStep(i)"
          >
            <Icon name="trash" :size="14" />
          </button>
        </div>
        <button class="alice-det__add-btn" type="button" @click="addNextStep">
          <Icon name="plus" :size="14" />
          <span>Add next step</span>
        </button>
      </section>

      <!-- ============== Danger zone ============== -->
      <section v-if="!isGuest" class="alice-det__section alice-det__danger">
        <header class="alice-det__section-head">
          <span
            class="alice-det__section-mark"
            style="background: var(--vp-rood-100)"
          />
          <h2 class="alice-det__section-title">Danger zone</h2>
          <p class="alice-det__section-sub">
            Het project verwijderen haalt ook alles eronder weg: opnames,
            momenten, insights, bestanden, notities, obslogs en chats. Dit kan
            niet ongedaan worden gemaakt.
          </p>
        </header>
        <button class="alice-det__danger-btn" type="button" @click="openDelete">
          <Icon name="trash" :size="16" />
          <span>Delete project</span>
        </button>
      </section>
    </div>

    <!-- ============== Delete confirmation ============== -->
    <Teleport to="body">
      <div v-if="deleteOpen" class="alice-del" @mousedown.self="closeDelete">
        <div
          class="alice-del__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="alice-del-title"
        >
          <header class="alice-del__head">
            <h2 id="alice-del-title" class="alice-del__title">
              Delete project
            </h2>
            <button
              class="alice-del__close"
              type="button"
              aria-label="Sluiten"
              :disabled="deleting"
              @click="closeDelete"
            >
              <Icon name="close" :size="20" />
            </button>
          </header>

          <div class="alice-del__body">
            <p class="alice-del__text">
              Je staat op het punt
              <strong>{{ deleteLabel }}</strong> permanent te verwijderen — met
              alle opnames, momenten, insights, bestanden, notities, obslogs en
              chats. Dit kan niet ongedaan worden gemaakt.
            </p>
            <label class="alice-det__field">
              <!-- The label is uppercased by the design, so the exact string to
                   type is shown in the placeholder, in its own casing. -->
              <span class="alice-det__label">{{ deleteHint }}</span>
              <input
                ref="deleteInput"
                v-model="deleteConfirm"
                class="alice-det__input"
                type="text"
                :placeholder="deletePhrase"
                :disabled="deleting"
                @keyup.enter="deleteReady && confirmDelete()"
              />
            </label>
            <p v-if="deleteError" class="alice-del__error">{{ deleteError }}</p>
          </div>

          <footer class="alice-del__foot">
            <button
              class="alice-del__cancel"
              type="button"
              :disabled="deleting"
              @click="closeDelete"
            >
              Cancel
            </button>
            <button
              class="alice-del__confirm"
              type="button"
              :disabled="!deleteReady || deleting"
              @click="confirmDelete"
            >
              {{ deleting ? "Deleting…" : "Delete project" }}
            </button>
          </footer>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { db, storage, functions } from "@/firebase";
import {
  doc,
  collection,
  updateDoc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";

import Icon from "@/components/alice/Icon.vue";
import Avatar from "@/components/alice/Avatar.vue";
import JobNumberPicker from "@/components/alice/JobNumberPicker.vue";
import GuestAccessSection from "@/components/GuestAccessSection.vue";
import { colorForOwner } from "@/components/alice/normalize";
import {
  jobNumberError,
  jobNumberErrorMessage,
  jobRowDetail,
  normaliseJobNumber,
  pickedKey,
  productiveRefLabel,
} from "@/utils/jobNumber";

export default {
  name: "ProjectDetails",
  components: { Icon, Avatar, GuestAccessSection, JobNumberPicker },
  data() {
    return {
      language: "Dutch",
      importing: false,
      clientMenu: false,
      newClientName: "",
      deleteOpen: false,
      deleteConfirm: "",
      deleting: false,
      deleteError: "",
      // The job number field: what is typed, the Productive row picked for
      // it, and what the last save said.
      jobDraft: "",
      jobPick: null,
      jobSaving: false,
      jobMessage: "",
      jobFailed: false,
    };
  },
  computed: {
    ...mapState({
      project: (state) => state.project,
      users: (state) => state.users,
      clients: (state) => state.clients,
    }),
    isGuest() {
      return this.$store.getters.isGuest;
    },
    deleteLabel() {
      return (this.project?.title || "").trim() || "dit project";
    },
    // Typing the project's own name is the confirmation. Untitled projects fall
    // back to a fixed word so there's still something deliberate to type.
    deletePhrase() {
      return (this.project?.title || "").trim() || "DELETE";
    },
    deleteHint() {
      return (this.project?.title || "").trim()
        ? "Typ de projectnaam om te bevestigen"
        : "Typ DELETE om te bevestigen";
    },
    deleteReady() {
      return (
        this.deleteConfirm.trim().toLowerCase() ===
        this.deletePhrase.toLowerCase()
      );
    },
    // A legacy free-text client_name with no client_id link yet — surfaced so it
    // can be mapped to (or created as) a real client.
    unlinkedName() {
      if (this.project && !this.project.client_id && this.project.client_name) {
        return this.project.client_name;
      }
      return null;
    },
    researchers() {
      const list = this.project?.researchers;
      if (!Array.isArray(list)) return [];
      return list.filter((r) => r && typeof r === "object");
    },
    availableUsers() {
      const list = Array.isArray(this.users) ? this.users : [];
      const taken = new Set(
        this.researchers.map((r) => r && (r.uid || r.email)).filter(Boolean)
      );
      return list.filter((u) => u && !taken.has(u.uid) && !taken.has(u.email));
    },
    subQuestions() {
      const list = this.project?.sub_research_questions;
      return Array.isArray(list) ? list : [];
    },
    nextSteps() {
      const list = this.project?.next_steps;
      return Array.isArray(list) ? list : [];
    },
    jobError() {
      return jobNumberError(this.jobDraft);
    },
    // Something to save: another number than the stored one, or the same
    // number picked as another Productive job.
    jobDirty() {
      if (!this.project) return false;
      const code = normaliseJobNumber(this.jobDraft);
      const stored = this.project.project_code || null;
      if (code !== stored) return Boolean(code) || Boolean(stored);
      const key = pickedKey(this.jobPick, this.jobDraft);
      const ref = this.project.productive_ref;
      return Boolean(key) && key !== (ref && ref.key);
    },
    jobPickLabel() {
      const row = this.jobPick;
      if (!row || !pickedKey(row, this.jobDraft)) return "";
      return [row.name, jobRowDetail(row)].filter(Boolean).join(" · ");
    },
    savedRefLabel() {
      return productiveRefLabel(this.project && this.project.productive_ref);
    },
  },
  watch: {
    // The stored job number, whenever another project (or a newer save) is
    // in the store.
    "project.uid": {
      immediate: true,
      handler() {
        this.resetJobNumber();
        this.jobMessage = "";
      },
    },
    "project.project_code"() {
      if (!this.jobSaving) this.resetJobNumber();
    },
    // Typing again: the last save's message no longer applies.
    jobDraft(value) {
      if (!this.jobMessage || this.jobSaving) return;
      const stored = (this.project && this.project.project_code) || null;
      if (normaliseJobNumber(value) !== stored) this.jobMessage = "";
    },
    deleteOpen(open) {
      if (open) {
        window.addEventListener("keydown", this.onDeleteEsc);
      } else {
        window.removeEventListener("keydown", this.onDeleteEsc);
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onDeleteEsc);
  },
  methods: {
    researcherColor(r) {
      return colorForOwner(r?.name || r?.email || "");
    },
    resetJobNumber() {
      this.jobDraft = (this.project && this.project.project_code) || "";
      this.jobPick = null;
      this.jobFailed = false;
    },
    // Saved at once, not with "Save changes": setProjectJobNumber stores the
    // job number with its Productive record and links the job's Drive folder.
    // The store then holds what it wrote, so a later Save does not put the
    // old values back.
    async saveJobNumber() {
      if (!this.project || !this.jobDirty || this.jobError || this.jobSaving) {
        return;
      }
      const uid = this.project.uid;
      const code = normaliseJobNumber(this.jobDraft);
      this.jobSaving = true;
      this.jobMessage = "";
      this.jobFailed = false;
      try {
        const call = httpsCallable(functions, "setProjectJobNumber", {
          timeout: 60000,
        });
        const res = await call({
          projectId: uid,
          jobNumber: code || "",
          productiveKey: pickedKey(this.jobPick, code),
          // The client on the form, saved or not: it decides which
          // Productive jobs and Drive folders are this client's.
          clientId: this.project.client_id || null,
          clientName: this.project.client_name || null,
        });
        this.$store.commit("setProjectFields", {
          uid,
          fields: res.data.fields,
        });
        if (this.project && this.project.uid === uid) {
          this.resetJobNumber();
          this.jobMessage = res.data.message;
        }
      } catch (err) {
        console.error("setProjectJobNumber failed:", err);
        this.jobFailed = true;
        this.jobMessage = jobNumberErrorMessage(err);
      } finally {
        this.jobSaving = false;
      }
    },
    // Link the project to a selected client, canonicalizing its client_name to
    // the client's spelling. Persisted by the project's "Save changes" flow.
    onClientSelect(uid) {
      if (!uid) {
        this.project.client_id = null;
        this.project.client_name = null;
        return;
      }
      const c = this.clients.find((x) => x.uid === uid);
      this.project.client_id = uid;
      if (c) this.project.client_name = c.name;
    },
    // Create a brand-new client from the popover and select it.
    async createAndSelectClient() {
      const name = this.newClientName.trim();
      if (!name) return;
      const id = await this.$store.dispatch("createClient", { name });
      this.onClientSelect(id);
      this.newClientName = "";
      this.clientMenu = false;
    },
    // Quick-create a client from a legacy imported name and link it.
    async createFromImported() {
      const name = (this.unlinkedName || "").trim();
      if (!name) return;
      const id = await this.$store.dispatch("createClient", { name });
      this.onClientSelect(id);
    },
    addSub() {
      if (!this.project) return;
      if (!this.project.sub_research_questions) {
        this.project.sub_research_questions = [];
      }
      this.project.sub_research_questions.push({ text: "" });
    },
    removeSub(i) {
      this.project.sub_research_questions.splice(i, 1);
    },
    addNextStep() {
      if (!this.project) return;
      if (!this.project.next_steps) this.project.next_steps = [];
      this.project.next_steps.push({ text: "" });
    },
    removeNextStep(i) {
      this.project.next_steps.splice(i, 1);
    },
    addResearcher(user) {
      if (!this.project) return;
      if (!this.project.researchers) this.project.researchers = [];
      this.project.researchers.push(user);
    },
    removeResearcher(i) {
      this.project.researchers.splice(i, 1);
    },
    async generateNextSteps() {
      this.$store.commit("setLoading", true);
      try {
        const generateNextSteps = httpsCallable(functions, "generateNextSteps");
        const res = await generateNextSteps({
          chapters: this.project.chapters,
          language: this.language,
        });
        this.project.next_steps = res.data.next_steps;
      } catch (err) {
        console.error("generateNextSteps failed:", err);
      } finally {
        this.$store.commit("setLoading", false);
      }
    },

    /* ============== Delete project ============== */
    openDelete() {
      this.deleteConfirm = "";
      this.deleteError = "";
      this.deleteOpen = true;
      this.$nextTick(() => this.$refs.deleteInput?.focus());
    },
    closeDelete() {
      if (this.deleting) return;
      this.deleteOpen = false;
    },
    onDeleteEsc(e) {
      if (e.key === "Escape") this.closeDelete();
    },
    async confirmDelete() {
      if (!this.deleteReady || this.deleting || !this.project?.uid) return;
      const uid = this.project.uid;
      this.deleting = true;
      this.deleteError = "";
      try {
        // Deleting the project doc is enough: the projectOnDelete trigger
        // recursively clears every subcollection and the Qdrant points.
        await deleteDoc(doc(db, "projects", uid));
        this.deleteOpen = false;
        await this.$router.replace("/projects");
        this.$store.commit("setProject", null);
        await this.$store.dispatch("fetchProjects");
      } catch (err) {
        console.error("deleteProject failed:", err);
        this.deleteError = `Verwijderen mislukt: ${err.message || err}`;
      } finally {
        this.deleting = false;
      }
    },

    /* ============== Setup PDF upload ============== */
    triggerSetupUpload() {
      if (this.$refs.uploadRef) this.$refs.uploadRef.click();
    },
    resetUploadValue(event) {
      // Allow re-uploading the same file twice in a row.
      if (event?.target) event.target.value = "";
    },
    async onFileChange() {
      const files = this.$refs.uploadRef?.files;
      if (!files || !files.length) return;
      this.importing = true;
      try {
        for (const key of Object.keys(files)) {
          await this.uploadSetup(files[key]);
        }
      } finally {
        this.importing = false;
      }
    },
    uploadSetup(file) {
      return new Promise((resolve, reject) => {
        (async () => {
          try {
            const fileExtension = file.name.split(".").pop();
            const docRef = await addDoc(
              collection(db, "projects", this.project.uid, "setup"),
              {
                title: file.name.split(".")[0],
                created: Math.round(Date.now() / 1000),
                images: [],
              }
            );
            await updateDoc(
              doc(db, "projects", this.project.uid, "setup", docRef.id),
              { uid: docRef.id }
            );

            const path = ref(
              storage,
              `projects/${this.project.uid}/setup/${docRef.id}/${docRef.id}.${fileExtension}`
            );
            const uploadTask = uploadBytesResumable(path, file);

            uploadTask.on(
              "state_changed",
              () => {
                /* progress hook reserved */
              },
              (error) => {
                console.error("Setup upload error", error);
                reject(error);
              },
              async () => {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref
                );
                await updateDoc(
                  doc(db, "projects", this.project.uid, "setup", docRef.id),
                  { storage_link: downloadURL }
                );
                const processSetup = httpsCallable(functions, "processSetup");
                await processSetup({
                  project_uid: this.project.uid,
                  pdf_link: downloadURL,
                  setup_uid: docRef.id,
                });
                resolve();
              }
            );
          } catch (err) {
            reject(err);
          }
        })();
      });
    },
  },
};
</script>

<style scoped lang="scss">
.alice-det {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-det__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-det__head-main {
  flex: 1;
  min-width: 0;
}
.alice-det__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-det__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-det__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 560px;
  line-height: 1.5;
}
.alice-det__head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.alice-det__head-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-black);
  border-radius: 0;
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-det__head-btn:hover:not(:disabled) {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-det__head-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-det__head-btn--small {
  height: 32px;
  padding: 0 12px;
  font-size: 11px;
  margin-left: auto;
}

/* ============== Body ============== */
.alice-det__body {
  display: flex;
  flex-direction: column;
  padding: 8px 32px 48px;
}
.alice-det__empty {
  padding: 32px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
}

/* ============== Section ============== */
.alice-det__section {
  border-bottom: 1px solid var(--border-hairline);
  padding: 28px 0 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.alice-det__section:last-child {
  border-bottom: none;
}
.alice-det__section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.alice-det__section-mark {
  width: 12px;
  height: 12px;
  background: var(--vp-black);
  flex-shrink: 0;
}
.alice-det__section-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--vp-black);
}
.alice-det__section-sub {
  flex-basis: 100%;
  margin: 0 0 0 24px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
  line-height: 1.5;
}

/* ============== Form atoms ============== */
.alice-det__grid {
  display: grid;
  gap: 16px 20px;
}
.alice-det__grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.alice-det__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.alice-det__field--span {
  grid-column: 1 / -1;
}
.alice-det__label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-det__count-pill {
  padding: 2px 8px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--fg-3);
  border: 1px solid var(--border-hairline);
}
.alice-det__sub-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.alice-det__input,
.alice-det__textarea {
  width: 100%;
  padding: 10px 12px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  outline: none;
  font-family: var(--font-body);
  font-size: 15px;
  line-height: 1.4;
  color: var(--vp-black);
  border-radius: 0;
}
.alice-det__input:focus,
.alice-det__textarea:focus {
  border-color: var(--vp-black);
}
.alice-det__input::placeholder,
.alice-det__textarea::placeholder {
  color: var(--fg-3);
}
.alice-det__input--mono {
  font-family: var(--font-mono);
  font-size: 14px;
}
.alice-det__textarea {
  resize: vertical;
  min-height: 64px;
}
.alice-det__textarea--mini {
  font-size: 14px;
  min-height: 56px;
}

/* Sharp native select */
.alice-det__select-wrap {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  color: var(--vp-black);
}
.alice-det__select-wrap:focus-within {
  border-color: var(--vp-black);
}
.alice-det__select {
  flex: 1;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: none;
  outline: none;
  padding: 10px 36px 10px 12px;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--vp-black);
  cursor: pointer;
  border-radius: 0;
}
.alice-det__select-wrap > .alice-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--fg-3);
}

/* ============== Row pattern ============== */
.alice-det__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.alice-det__row-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 28px;
  background: var(--vp-grijs-20);
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--fg-3);
  flex-shrink: 0;
  margin-top: 4px;
}
.alice-det__row-del {
  margin-top: 4px;
}

/* Generic icon button */
.alice-det__icon-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--vp-black);
  border-radius: 0;
  flex-shrink: 0;
  transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease;
}
.alice-det__icon-btn:hover {
  border-color: var(--vp-black);
}
.alice-det__icon-btn--danger {
  color: var(--vp-rood-100);
}
.alice-det__icon-btn--danger:hover {
  border-color: var(--vp-rood-100);
  background: var(--vp-rood-10);
}

/* Add buttons */
.alice-det__add-btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--vp-white);
  border: 1px dashed var(--vp-grijs-60);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-2);
  border-radius: 0;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.alice-det__add-btn:hover {
  border-color: var(--vp-black);
  border-style: solid;
  color: var(--vp-black);
}
.alice-det__add-btn--solid {
  border-style: solid;
  border-color: var(--vp-black);
  color: var(--vp-black);
}
.alice-det__add-btn--solid:hover {
  background: var(--vp-black);
  color: var(--vp-white);
}

.alice-det__hint {
  margin: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  font-style: italic;
  color: var(--fg-3);
}

/* ============== Client picker ============== */
.alice-det__client-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}
.alice-det__client-select {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}
.alice-det__add-client {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  flex-shrink: 0;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--vp-black);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-hover);
}
.alice-det__add-client:hover {
  background: var(--bg-muted);
}
.alice-det__client-pop {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  width: 260px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.alice-det__client-pop-label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-det__client-pop-btn {
  padding: 8px 12px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: 1px solid var(--vp-black);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.alice-det__client-pop-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-det__link-create {
  background: none;
  border: none;
  padding: 0;
  margin-left: 4px;
  color: var(--vp-blauw-100, #1f6feb);
  font-family: var(--font-ui);
  font-size: 12px;
  font-style: normal;
  cursor: pointer;
}

/* ============== Job number ============== */
.alice-det__job-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin: 0;
}
.alice-det__job-picker {
  flex: 1;
  min-width: 0;
}
.alice-det__job-save {
  height: 42px;
}
.alice-det__job-save:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-det__job-save:disabled:hover {
  background: var(--vp-white);
}
.alice-det__job-msg {
  font-style: normal;
  color: var(--fg-2);
}
.alice-det__job-msg--error {
  color: var(--vp-rood-100);
}

/* ============== Researchers ============== */
.alice-det__researchers {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.alice-det__researcher {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px 8px 8px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  min-width: 280px;
}
.alice-det__researcher-text {
  flex: 1;
  min-width: 0;
}
.alice-det__researcher-name {
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 700;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-det__researcher-email {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ============== Danger zone ============== */
.alice-det__danger {
  margin-top: 8px;
  border-top: 1px solid var(--vp-rood-100);
}
/* The red rule replaces the previous section's hairline — without this they
   stack as two lines. */
.alice-det__section:has(+ .alice-det__danger) {
  border-bottom: none;
}
.alice-det__danger-btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  background: var(--vp-white);
  border: 1px solid var(--vp-rood-100);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-rood-100);
  border-radius: 0;
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-det__danger-btn:hover {
  background: var(--vp-rood-100);
  color: var(--vp-white);
}

/* ============== Delete dialog ============== */
.alice-del {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(20, 25, 25, 0.42);
}
.alice-del__panel {
  width: 480px;
  max-width: 100%;
  max-height: 100%;
  overflow-y: auto;
  background: var(--vp-white);
  border: 1.5px solid var(--vp-black);
  box-shadow: 0 18px 48px rgba(20, 25, 25, 0.22);
}
.alice-del__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1.5px solid var(--vp-grijs-20);
}
.alice-del__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-del__close {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: none;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--vp-black);
}
.alice-del__close:hover:not(:disabled) {
  border-color: var(--vp-black);
}
.alice-del__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
}
.alice-del__text {
  margin: 0;
  font-family: var(--font-body);
  font-size: 15px;
  line-height: 1.5;
  color: var(--fg-2);
}
.alice-del__error {
  margin: 0;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--vp-rood-100);
}
.alice-del__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px 20px;
  border-top: 1.5px solid var(--vp-grijs-20);
}
.alice-del__cancel,
.alice-del__confirm {
  height: 40px;
  padding: 0 16px;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 0;
}
.alice-del__cancel {
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  color: var(--vp-black);
}
.alice-del__cancel:hover:not(:disabled) {
  border-color: var(--vp-black);
}
.alice-del__confirm {
  background: var(--vp-rood-100);
  border: 1px solid var(--vp-rood-100);
  color: var(--vp-white);
}
.alice-del__confirm:disabled,
.alice-del__cancel:disabled,
.alice-del__close:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ============== Responsive ============== */
@media (max-width: 1279px) {
  .alice-det__head {
    padding: 24px 20px 20px;
    flex-direction: column;
  }
  .alice-det__head-actions {
    align-self: flex-start;
  }
  .alice-det__body {
    padding: 8px 20px 32px;
  }
  .alice-det__grid--two {
    grid-template-columns: 1fr;
  }
}
</style>
